import React from 'react'
import * as Sentry from '@sentry/nextjs'
import { Controller, useForm } from 'react-hook-form'
import Image from 'next/image'
import Select from 'react-select';
import styled, { css } from 'styled-components'
import { breakpoint } from '../layout/styles'
import { Container } from '../styles/Container'
import { Grid, Cell } from '../styles/Grid'
import ReactHtmlParser from 'react-html-parser'
import { Wysiwyg } from "../styles/Wysiwyg"
import { useMediaQuery } from '../helpers/mq'
import { sup } from '../helpers/string'
import { Button } from '../layout/Common'
import states from '../assets/states.json';
import { useRouter } from 'next/router';

const ContactImproved = ({ data, translations }: any) => {
  const router = useRouter()
  const { register, setValue, formState: { errors }, handleSubmit, control } = useForm()
  const [submitted, setSubmitted] = React.useState(false)
  const isMobile = useMediaQuery(breakpoint.desktop_down)
  const defaultFieldRequiredText = 'This field is required'
  let countryCode = 'US'

  if (router?.locale !== 'en-US') {
    countryCode = 'CA'
  }

  const stateOptions = states
    .filter((state) => state.country_code === countryCode)
    .map((state) => ({
      value: state.state_code,
      label: state.name,
    }))

  let subjectOptions = [];
  if (data.subject) {
    const lines = data.subject.split('\n');
    subjectOptions = lines.map((line: string) => {
      const [label, value] = line.split(':');
      return { label, value };
    });
  }

  // we'll use this in a different place on mobile
  const description = ReactHtmlParser(sup(data.description ?? 'Description'))

  async function handleOnSubmit(data: any) {
    setSubmitted(true);
    // Get the selected subject label from the subject object
    const selectedSubjectLabel = data.subject.label;

    // Get the labels of the selected states and join them into a comma-separated string
    const selectedStatesLabels = data.states.map((state: any) => state.label).join(', ');

    // Create a new object with the selected subject label as a string
    const formData = {
      ...data,
      subject: selectedSubjectLabel,
      states: selectedStatesLabels
    };
    await fetch('/api/mail', {
      method: 'POST',
      body: JSON.stringify(formData)
    }).catch((error: any) => {
      console.error(error)
      Sentry.captureException(error)
    });
  }

  return (
    <Wrapper>
      <SectionTitle>{data.title}</SectionTitle>
      {description && !isMobile &&
        <Description>{description}</Description>
      }
      <Content>
        <ContactInfo>
          {data.address &&
            <InfoRow>
              <InfoImage>
                <Image
                  src={"/images/position.svg"}
                  layout={"fill"}
                />
              </InfoImage>
              <InfoColumn>
                <InfoTitle>{translations.address ?? 'Address'}</InfoTitle>
                <InfoDetail>{ReactHtmlParser(data.address)}</InfoDetail>
              </InfoColumn>
            </InfoRow>
          }
          {data.phone &&
            <InfoRow>
              <InfoImage>
                <Image
                  src={"/images/phone.svg"}
                  layout={"fill"}
                />
              </InfoImage>
              <InfoColumn>
                <InfoTitle>{translations.phone ?? 'Phone'}</InfoTitle>
                <InfoDetail>{ReactHtmlParser(data.phone)}</InfoDetail>
              </InfoColumn>
            </InfoRow>
          }
        </ContactInfo>
        <FormOuterWrapper>
          <FormWrapper>
            {submitted && data.thanks && (
              <ThankYouMessage>{ReactHtmlParser(data.thanks)}</ThankYouMessage>
            )}
            {!submitted &&
              <form onSubmit={handleSubmit(handleOnSubmit)}>
                <input
                  type="hidden"
                  value={data.email}
                  {...register("from")} />
                <input
                  type="hidden"
                  value={data.email_to}
                  {...register("to")} />
                <FormRow>
                  <FormField>
                    <Label htmlFor={'name'} required={true}>{translations.name ?? "Name"}</Label>
                    <input
                      type="text"
                      title='Name'
                      id="name"
                      aria-invalid={errors.name ? "true" : "false"}
                      {...register("name", {
                        required: translations.field_required_message ?? defaultFieldRequiredText
                      })} />
                    {errors.name &&
                      <ErrorMessage>
                        {errors.name.message}
                      </ErrorMessage>
                    }
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField>
                    <Label htmlFor={'email'} required={true}>{translations.email ?? 'Email'}</Label>
                    <input
                      type="text"
                      title="Email"
                      id="email"
                      aria-invalid={errors.email ? "true" : "false"}
                      {...register("email", {
                        required: translations.field_required_message ?? defaultFieldRequiredText,
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                          message: translations.invalid_email_message ?? 'Invalid email'
                        }
                      })} />
                    {errors.email &&
                      <ErrorMessage>
                        {errors.email.message}
                      </ErrorMessage>
                    }
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField>
                    <Label htmlFor={'phone'} required={true}>
                      {translations.phone_number ?? 'Phone'}
                    </Label>
                    <input
                      type="tel"
                      title='Phone'
                      id="phone"
                      pattern="[0-9]+"
                      {...register("phone", {
                        required: translations.field_required_message ?? defaultFieldRequiredText,
                      })} />
                    {errors.phone &&
                      <ErrorMessage>
                        {errors.phone.message}
                      </ErrorMessage>
                    }
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField>
                    <Label htmlFor={'states'} required={true}>
                      {translations.states ?? 'State(s) where business is conducted (select all that apply)'}
                    </Label>
                    <ContactFormSelect>
                      <Controller
                        name="states"
                        control={control}
                        rules={{ required: translations.field_required_message ?? defaultFieldRequiredText }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            isMulti
                            title="States"
                            id="states"
                            options={stateOptions}
                          />
                        )}
                      />
                    </ContactFormSelect>
                    {errors.states && (
                      <ErrorMessage>{errors.states.message}</ErrorMessage>
                    )}
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField>
                    <Label htmlFor={'subject'} required={true}>
                      {translations.subject ?? 'Subject'}
                    </Label>
                    <ContactFormSelect>
                      <Controller
                        name="subject"
                        control={control}
                        rules={{ required: translations.field_required_message ?? defaultFieldRequiredText }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            title="Subject"
                            id="subject"
                            onChange={(selectedOption) => {
                              field.onChange(selectedOption)
                              const originalOption: any = subjectOptions.find((option: any) => option.label === selectedOption?.label)

                              if (originalOption && originalOption.value) {
                                setValue("to", originalOption.value)
                              }
                            }}
                            options={subjectOptions.map((option: any) => ({
                              value: option.label,
                              label: option.label,
                            }))}
                          />
                        )}
                      />
                    </ContactFormSelect>
                    {errors.subject && (
                      <ErrorMessage>{errors.subject.message}</ErrorMessage>
                    )}
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField>
                    <Label htmlFor={'message'} required={true}>
                      {translations.message ?? 'Message'}
                    </Label>
                    <textarea
                      rows={6}
                      title="Message"
                      id="message"
                      {...register("message", {
                        required: translations.field_required_message ?? defaultFieldRequiredText,
                      })} />
                    {errors.message &&
                      <ErrorMessage>
                        {errors.message.message}
                      </ErrorMessage>
                    }
                  </FormField>
                </FormRow>
                <FormRow flexDirection={"column"}>
                  <FormField>
                    <FormCheckBox>
                      <input
                        type="checkbox"
                        title="Terms"
                        id="terms"
                        aria-invalid={errors.terms ? "true" : "false"}
                        {...register("terms", {
                          required: translations.accept_terms_message ?? ''
                        })} />
                      <Label htmlFor={'terms'} size={"small"}>
                        {
                          translations.accept_terms ? ReactHtmlParser(translations.accept_terms)
                            : 'I agree to High Liner\'s Terms of Use and Privacy Policy.'
                        }
                      </Label>
                    </FormCheckBox>
                    {errors.terms &&
                      <ErrorMessage>
                        {errors.terms.message}
                      </ErrorMessage>
                    }
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField>
                    <Submit type="submit">
                      {translations.send_button ?? 'Submit'}
                    </Submit>
                  </FormField>
                </FormRow>
              </form>
            }
          </FormWrapper>
        </FormOuterWrapper>
      </Content>
    </Wrapper>
  )
}

export default ContactImproved

const Wrapper = styled(Container)`
  display: flex;
  flex-direction: column;
  text-align: center;
  && {
    margin-top: 0;
    margin-bottom: 0;
    padding-top: 3rem;
    padding-bottom: 3.75rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  
    @media ${breakpoint.desktop}{
      text-align: left;
      padding-top: 3.75rem;
      padding-bottom: 6rem;
    }  

    @media ${breakpoint.desktop_down} {
      max-width: 45rem;
      padding-top: 2.75rem;
      // less padding since icons have some margin bottom
      padding-bottom: 0.75rem;
    }
  }
`

const SectionTitle = styled.h1`
  font-weight: 900;
  font-size: 2.875rem;
  line-height: 1.5rem;
  text-transform: capitalize;
  @media ${breakpoint.tablet} {
    font-size: 3.75rem;
    line-height: 2.5rem;
  }
`

const Content = styled.div<any>`
  display: flex;
  flex-direction: column;
  @media ${breakpoint.desktop} {
    flex-direction: row;
  }
`

const ContactInfo = styled.div<any>`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: space-around;
  flex: 1;
  @media ${breakpoint.desktop} {
    justify-content: flex-start;
    padding-right: 2rem;
  }
`

const Description = styled.div<any>`
  margin-bottom: 2.25rem;
  width: 100%;
`

const InfoRow = styled.div<any>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 1.5rem 3rem;
  @media ${breakpoint.tablet} {
    flex-direction: row;
    align-items: flex-start;
  }
  @media ${breakpoint.desktop} {
    margin: 0 0 5rem;
  }
`

const InfoImage = styled.div<any>`
  position: relative;
  height: 4rem;
  width: 4rem;
  margin-bottom: 1rem;
  @media ${breakpoint.tablet} {
    height: 6rem;
    width: 4.375rem;
    margin-right: 2rem;
  }
  @media ${breakpoint.desktop} {
    margin-bottom: 0;
    margin-right: 4.5rem;
  }
`

const InfoColumn = styled(Wysiwyg) <any>`
  display: flex;
  flex-direction: column;
  align-items: center;
  @media ${breakpoint.tablet} {
    align-items: flex-start;
  }
  
  p {
    margin-bottom: 0.25rem;
 }
`

const InfoTitle = styled.span<any>`
  font-size: 1.5625rem;
  font-weight: 800;
  line-height: 1.875rem;
  color: var(--primary);
  margin-bottom: 1rem;
  @media ${breakpoint.desktop} {
    margin-bottom: 1.5rem;
  }
`

const InfoDetail = styled.span<any>`
  font-weight: 700;
  text-align: center;
  @media ${breakpoint.tablet} {
    text-align: start;
  }
`

// holds FormWrapper and a Description (on mobile only).
const FormOuterWrapper = styled.div`
  flex: 1;
  // Put form above the icons on mobile. I tried wrapping the form into a component
  // and rendering it in a different place on mobile, but it seemed to cause bugs
  // with input fields losing focus when typing into them.
  order: -1;
  @media ${breakpoint.desktop}{
    order: 1;
  }
`

const FormWrapper = styled.div<any>`
  margin-bottom: 4rem;
  @media ${breakpoint.desktop} {
    margin-bottom: 0;
  }
  form {
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
    background-color : var(--lightblue);
    border-bottom-right-radius: 1.25rem;
  }

  input,
  textarea,
  select {
    color: var(--input-color);
    font-family: var(--body-font);
    line-height: 1;
    padding: 0.6875rem 1.25rem 0.75rem;
    background-color: var(--white);
    border: 1px solid var(--input-border-color);
    border-radius: 1rem;
    transition: border var(--transition-sm);
    margin-bottom: 0.7rem;

    &::placeholder {
      color: var(--input-placeholder-color);
    }
    
    &:hover {
      border-color: var(--input-border-color-hover);
    }

    &:focus {
      border-color: var(--input-border-color-focus);
      outline: none;
    }
  }
`

const ContactFormSelect = styled.div`
  &&& > div > div {
    border-radius: 1rem;
  }
`

const ThankYouMessage = styled.div``

const FormRow = styled(Grid)`
  flex-direction: ${props => props.flexDirection ?? 'row'};
  width: 100%;
  margin: 0 auto;
`

const FormField = styled(Cell)`
  display: flex;
  flex-direction: column;
  justify-content: start;
  margin: .7rem 0;
`

const FormCheckBox = styled.div<any>`
  display: flex;
  flex-direction: row;
  align-items: center;

  label {
    margin-bottom: 0;
  }
  input {
    width: 1.5rem;
    margin-bottom: 0.325rem;
  }
`

const ErrorMessage = styled.span<any>`
  text-align: start;
  color: var(--orange);
  padding-top: .5rem;
`

const Submit = styled(Button)`
  max-width: 10.3125rem;
  align-self: center;
  @media ${breakpoint.tablet} {
    align-self: flex-start;
  }
`

const Label = styled.label<any>`
  line-height: 1.5rem;
  font-weight: 700;
  margin-bottom: .5rem;
  text-align: start;

  p {
    ${props => props.size === "small" && css`
      font-size: 1rem;
      line-height: 1.625rem;
      font-weight: 400;
    `}
  }
`
